import { computed, makeObservable } from 'mobx';

import { hasValue } from 'src/shared/utils/common';
import type { Directories } from 'src/store/directories/directories.store';

import type { TOption } from '../types';

type TGetValidSelectValue = (fieldId: 'coreSamplingIntervalId', value: number[]) => (number | TOption)[];

export class GeologicalLoadViewModel {
  private readonly directories: Directories;

  constructor(directories: Directories) {
    this.directories = directories;

    makeObservable(this);
  }

  @computed
  get getValidCoreSamplingIntervalValue(): (value: number[]) => (number | TOption)[] {
    const coreSamplingIntervalArchivedOptionsMap = new Map(
      this.coreSamplingIntervalOptions.archived.map((v) => [v.value, v]),
    );

    return (value) => {
      return value.map((v) => coreSamplingIntervalArchivedOptionsMap.get(v) ?? v);
    };
  }

  getValidSelectValue: TGetValidSelectValue = (fieldId, value) => {
    switch (fieldId) {
      case 'coreSamplingIntervalId': {
        if (!value.length) {
          return [];
        }

        return this.getValidCoreSamplingIntervalValue(value);
      }
    }
  };

  @computed
  get coreSamplingIntervalOptions(): { actual: TOption<number>[]; archived: TOption<number>[] } {
    const coreSamplingIntervalDirectory = this.directories.getObject('Common_CoreSamplingInterval');

    if (!coreSamplingIntervalDirectory) {
      console.error('coreSamplingIntervalDirectory is not presented');
      return { archived: [], actual: [] };
    }

    return coreSamplingIntervalDirectory.reduce(
      (acc: { actual: TOption<number>[]; archived: TOption<number>[] }, dir) => {
        const isArchived = dir.status === 'DELETED';
        const option = { label: dir.data['name'] as string, value: dir.id as number };

        if (isArchived) {
          acc.archived.push(option);
        } else {
          acc.actual.push(option);
        }

        return acc;
      },
      { archived: [], actual: [] },
    );
  }

  @computed
  get getSectionTypeFn(): (section: { sectionTypeId: number | null }) => string {
    const sectionTypesMap = (() => {
      const sectionTypesDirectory = this.directories.getObject('Common_SectionType');

      return new Map<number, string>(
        sectionTypesDirectory?.map((dir) => [dir.id as number, dir.data['name'] as string]),
      );
    })();

    return (section): string => {
      if (!hasValue(section.sectionTypeId)) {
        return 'geologicalLoada:errors.unrecognizedSectionName';
      }

      return sectionTypesMap.get(section.sectionTypeId) ?? 'geologicalLoada:errors.unrecognizedSectionName';
    };
  }
}
